import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { getCloudinaryUrl } from '../../utils/image';
import { personName } from '../../utils/people_helpers';

import './avatar.scss';

const AVATAR_TYPES = {
  error: 'error',
  message: 'message',
};

const AVATAR_SIZES = {
  xlarge: 'xlarge',
  large: 'large',
  medium: 'medium',
  small: 'small',
  xsmall: 'xsmall',
  xxsmall: 'xxsmall',
  xxxsmall: 'xxxsmall',
};

const SPACE_CHAR = ' ';
const EMPTY_STRING = '';

const getInitials = (name, user) => {
  let userName = '';
  const firstName = _.chain(user)
    .get('first_name', EMPTY_STRING)
    .split(SPACE_CHAR)
    .head()
    .value();
  const lastName = _.chain(user)
    .get('last_name', EMPTY_STRING)
    .split(SPACE_CHAR)
    .head()
    .value();

  if (!userName && (firstName || lastName)) {
    userName = _.join([firstName, lastName], SPACE_CHAR);
  } else if (!userName) {
    userName = name;
  }

  const namesArray = _.chain(userName)
    .replace(/[^\w ]/gi, EMPTY_STRING)
    .split(SPACE_CHAR);

  if (_.size(namesArray) > 1) {
    return namesArray
      .filter(word => !_.isEmpty(word))
      .slice(0, 2)
      .map(word => _.head(word))
      .join('')
      .toUpper()
      .value();
  }

  return namesArray
    .head()
    .slice(0, 2)
    .join('')
    .toUpper()
    .value();
};

const getInitialsCompany = name =>
  _.chain(name)
    .replace(/[^\w ]/gi, EMPTY_STRING)
    .first()
    .toUpper()
    .value();

const getAvatarContent = (url, name, size, icon, type, user, text) => {
  if (type === AVATAR_TYPES.error) return <span className="icon-exclamation" />;
  if (text) return <span>{text}</span>;
  if (!_.isEmpty(url)) {
    let imageUrl = url;
    if (_.isObject(imageUrl)) {
      imageUrl = getCloudinaryUrl(`${imageUrl.bucket}/${imageUrl.photo_key}`);
    }

    return <img className="avatar__img" src={imageUrl} alt="user-avatar" />;
  }
  if (icon) return <span className={`icon-${icon}`} />;

  if (name || user) {
    return <span>{getInitials(name, user)}</span>;
  }

  return <span className="icon-user" />;
};

const getAvatarFromPerson = (person, type) => {
  if (type === AVATAR_TYPES.error) return <span className="icon-exclamation" />;
  const company = _.get(person, 'company');
  const user = _.get(person, 'user');
  const name = personName(person);

  if (!_.isEmpty(company)) {
    const logoBucket = _.get(company, 'logo.bucket');
    const photoKey = _.get(company, 'logo.photo_key');
    if (logoBucket && photoKey) {
      const avatarUrl = getCloudinaryUrl(`${logoBucket}/${photoKey}`);

      return <img className="avatar__img" src={avatarUrl} alt="user-avatar" />;
    }

    return <span>{getInitialsCompany(_.get(company, 'name'))}</span>;
  }

  if (name || user) {
    return <span>{getInitials(name, user)}</span>;
  }

  return <span className="icon-user" />;
};

export const Avatar = props => {
  const {
    accepted,
    assigned,
    autoMessage,
    className,
    declined,
    expired,
    icon,
    name,
    pending,
    person,
    size,
    text,
    title,
    type,
    url,
    user,
  } = props;

  return (
    <div
      className={classnames('avatar', className, {
        'avatar--accepted': accepted,
        'avatar--assigned': assigned,
        'avatar--auto-message': autoMessage,
        'avatar--declined': declined,
        'avatar--expired': expired,
        'avatar--pending': pending,
        'avatar--exclamation': type === AVATAR_TYPES.error,
        'avatar--small': size === AVATAR_SIZES.small,
        'avatar--xsmall': size === AVATAR_SIZES.xsmall,
        'avatar--xxsmall': size === AVATAR_SIZES.xxsmall,
        'avatar--xxxsmall': size === AVATAR_SIZES.xxxsmall,
        'avatar--medium': size === AVATAR_SIZES.medium,
        'avatar--large': size === AVATAR_SIZES.large,
        'avatar--xlarge': size === AVATAR_SIZES.xlarge,
        'avatar--with-icon': icon,
      })}
      title={title}
    >
      {autoMessage ? (
        <span className="icon-autorobot" />
      ) : (
        <>
          <div className="avatar__status" />
          {!person
            ? getAvatarContent(url, name, size, icon, type, user, text)
            : getAvatarFromPerson(person, type)}
        </>
      )}
    </div>
  );
};

Avatar.propTypes = {
  accepted: PropTypes.bool,
  assigned: PropTypes.bool,
  autoMessage: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  declined: PropTypes.bool,
  expired: PropTypes.bool,
  icon: PropTypes.string,
  name: PropTypes.string,
  pending: PropTypes.bool,
  size: PropTypes.oneOf(_.values(AVATAR_SIZES)),
  text: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }),
  person: PropTypes.shape({
    company: PropTypes.shape({}),
    user: PropTypes.shape({}),
  }),
};

Avatar.defaultProps = {
  icon: null,
  name: null,
  size: 'xsmall',
  type: 'message',
  url: null,
  user: null,
  person: null,
};

export default Avatar;
